/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react/lib/Icon';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { formatNumberOfDocuments } from 'src/core/utils/formatNumberOfDocuments';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { LuisConstants } from 'src/core/utils/LuisConstants';

type TrainingLimitsUIProps = {
    id?: string;
};

const messages = defineMessages({
    readyForTraining: {
        id: 'customText.trainingLimitsUI.readyForTraining',
        defaultMessage: 'Ready for training'
    },
    notReadyForTraining: {
        id: 'customText.trainingLimitsUI.notReadyForTraining',
        defaultMessage: 'Not ready for training'
    },
    progressLabel: {
        id: 'customText.trainingLimitsUI.progressLabel',
        defaultMessage: 'Files labeled for training'
    },
    progressDescription: {
        id: 'customText.trainingLimitsUI.progressDescription',
        defaultMessage: '{ labeled } of { total } labeled files'
    },
    tagsRemainingNote: {
        id: 'customText.trainingLimitsUI.tagsRemainingNote',
        defaultMessage: 'You need to label { number } more files before you can train your model.'
    },
    learnMore: {
        id: 'customText.trainingLimitsUI.learnMore',
        defaultMessage: 'Learn more about this requirement.'
    }
});

const scopedTheme = getScopedTheme('TrainingLimitsUI');
const { styleComponent } = getStylistV2('TrainingLimitsUI');

const TopHeader = styleComponent<IStackProps>(Stack)('TopHeader', theme => ({
    minHeight: 38,
    cursor: 'pointer',
    padding: '0 12px',
    marginBottom: '2px',
    background: theme.headerColor
}));

const DetailsContainer = styleComponent<IStackProps>(Stack)('DetailsContainer', theme => ({
    padding: 10,
    borderBottom: `solid 1px ${theme.separatorColor}`
}));

const minimumTags = LuisConstants.CustomText.MinimumNumberOfTaggedDocuments;

export const TrainingLimitsUI = observer((props: TrainingLimitsUIProps) => {
    const { id = getId() } = props;
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const [isExpanded, setIsExpanded] = React.useState(false);
    const totalDocuments = rootStore.currentCustomTextProjectStore.documents.length;
    const taggedDocuments = rootStore.currentCustomTextProjectStore.labeledDocumentsCount;
    const isReadyForTraining = taggedDocuments >= minimumTags;
    const canLoadMoreBlobs = rootStore.currentCustomTextProjectStore.nextBatchExists;

    return (
        <div id={id}>
            <TopHeader
                horizontal
                tabIndex={0}
                verticalAlign="center"
                tokens={{ childrenGap: 12 }}
                onClick={() => setIsExpanded(state => !state)}
                onKeyDown={e => {
                    if (e.key === LuisKeyCodes.Enter) {
                        setIsExpanded(state => !state);
                    }
                }}>
                {isReadyForTraining ? (
                    <Icon iconName="Completed" styles={{ root: { color: scopedTheme.successColor } }} />
                ) : (
                    <Icon iconName="Info" />
                )}

                <LuisText styles={{ root: { flex: 1 } }}>
                    {isReadyForTraining ? localize(messages.readyForTraining) : localize(messages.notReadyForTraining)}
                </LuisText>

                <Icon iconName={isExpanded ? 'ChevronUpMed' : 'ChevronDownMed'} />
            </TopHeader>

            {isExpanded && (
                <DetailsContainer tokens={{ childrenGap: 8 }}>
                    <ProgressIndicator
                        label={localize(messages.progressLabel)}
                        percentComplete={taggedDocuments / minimumTags}
                        description={localize(messages.progressDescription, {
                            labeled: taggedDocuments,
                            total: formatNumberOfDocuments(totalDocuments, canLoadMoreBlobs)
                        })}
                    />

                    {minimumTags > taggedDocuments && (
                        <LuisText as="p">{localize(messages.tagsRemainingNote, { number: minimumTags - taggedDocuments })}</LuisText>
                    )}
                </DetailsContainer>
            )}
        </div>
    );
});
